import { useState, useEffect, useMemo, useCallback } from 'react';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import { useCompanyList } from '../../../stores/companyStore';
import { fleetApi } from '../../../api/axiosClient';
// import { useMasterVehicleList } from '../../../stores/masterVehicleStore';
import { Chip } from '@mui/material';
import LicenseDetailEditDialog from '../components/LicenseDetailEditDialog';
import { useLanguage } from '../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function VehicleLicensePageContent() {
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [allRows, setAllRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const [searchField, setSearchField] = useState('license_plate');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    return stored ? [{ value: stored, label: stored }] : [];
  });

  const companyOptionsNormalized = useMemo(() => {
    return (companyOptions || []).map((c) => ({
      value: String(c.value ?? c.id ?? c),
      label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
    }));
  }, [companyOptions]);

  // const { data: masterVehicles = [], isLoading: isVehicleLoading } = useMasterVehicleList(0, 1000, selectedCompanies);
  // const vehicleIdsString = useMemo(() => {
  //   if (!masterVehicles || masterVehicles.length === 0) return '';
  //   const ids = masterVehicles.map((v) => v.id || v.vehicle_id).filter(Boolean);
  //   return ids.join(',');
  // }, [masterVehicles]);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('license.all', 'All') },
      { value: 'license_plate', label: t('license.plateNumber', 'Plate Number'), table_name: 'vehicle' },
      {
        value: 'vehicle_license_license_name',
        label: t('license.documentName', 'Document Name'),
        table_name: 'vehicle_license',
        name: 'license_name'
      },
      { value: 'license_no', label: t('license.documentNumber', 'Document Number') }
      // { value: 'license_exp_date', label: t('license.expiredDate', 'Expired Date') },
      // { value: 'day_left', label: t('license.dayLeft', 'Day Left') }
    ],
    [t]
  );

  const joinDefs = useMemo(
    () => [
      { name: 'vehicle', column_results: ['name', 'license_plate'] },
      { name: 'vehicle_license', column_results: ['license_name', 'is_lifetime'] }
    ],
    []
  );

  const orderDefs = useMemo(
    () => ({
      columns: ['status_notif', 'license_exp_date'],
      ascending: true
    }),
    []
  );

  const fetchData = useCallback(async () => {
    // if (isVehicleLoading) return;
    setLoading(true);
    const controller = new AbortController();

    try {
      const start = paginationModel.page * paginationModel.pageSize;
      const length = paginationModel.pageSize;

      const payload = {
        paging: { start, length },
        orders: orderDefs,
        joins: joinDefs,
        columns: [{ name: 'status', logic_operator: '=', value: 'open', operator: 'AND' }]
      };

      if (statusFilter !== '') {
        payload.columns.push({
          name: 'status_notif',
          logic_operator: '=',
          value: String(statusFilter),
          operator: 'AND'
        });
      }

      let filters = [];
      if (searchValue) {
        if (searchField === 'all') {
          filters = [
            { name: 'license_no', value: searchValue },
            // { name: 'license_exp_date', value: searchValue },
            // { name: 'day_left', value: searchValue },
            { name: 'license_plate', table_name: 'm_vehicle', value: searchValue },
            { name: 'license_name', table_name: 'm_vehicle_license', value: searchValue }
          ];
          payload.filters = filters;
        } else {
          const opt = searchFieldOptions.find((o) => o.value === searchField);
          if (opt) {
            payload.columns.push({
              name: opt.name || opt.value,
              logic_operator: 'like',
              value: searchValue,
              operator: 'AND',
              table_name: opt.table_name
            });
          } else {
            payload.columns.push({
              name: searchField,
              logic_operator: 'like',
              value: searchValue,
              operator: 'AND'
            });
          }
        }
      }
      if (selectedCompanies && selectedCompanies.length > 0) {
        const companyString = Array.isArray(selectedCompanies)
          ? selectedCompanies.map((c) => c?.value ?? c?.id ?? c).join(', ')
          : (selectedCompanies?.value ?? selectedCompanies?.id ?? selectedCompanies);
        console.log('Compani', companyString);
        payload.columns.push({
          name: 'company_id',
          logic_operator: 'in',
          value: companyString || '-1',
          table_name: 'm_vehicle',
          operator: 'AND'
        });
      }
      if (payload.columns.length > 0) {
        payload.columns.push(
          { name: 'license_plate', logic_operator: 'IS NOT NULL', operator: 'AND', table_name: 'm_vehicle' },
          { name: 'license_plate', logic_operator: '<>', operator: 'AND', table_name: 'm_vehicle' },
          { name: 'license_name', logic_operator: 'IS NOT NULL', operator: 'AND', table_name: 'm_vehicle_license' },
          { name: 'license_name', logic_operator: '<>', operator: 'AND', table_name: 'm_vehicle_license' }
        );
      }

      const resp = await fleetApi.post('/vehicle-license-detail/search' + getTzParam(), payload, {
        signal: controller.signal
      });
      const data = resp?.data ?? null;
      const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
      const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

      setAllRows(fetchedRows);
      setTotalRecords(total);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Failed to fetch vehicle license data', err);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }

    return () => controller.abort();
  }, [
    // isVehicleLoading,
    selectedCompanies,
    searchValue,
    searchField,
    joinDefs,
    orderDefs,
    // vehicleIdsString,
    statusFilter,
    paginationModel.page,
    paginationModel.pageSize,
    searchFieldOptions
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(searchRaw ?? '');
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(t);
  }, [searchRaw]);

  const visibleRows = useMemo(() => {
    return allRows;
  }, [allRows]);

  const handlePageChange = (model) => {
    setPaginationModel(model);
  };

  const handleSearch = ({ field, value }) => {
    if (field === 'status') {
      setStatusFilter(value);
      setPaginationModel((p) => ({ ...p, page: 0 }));
      return;
    }
    setSearchField(field || 'all');
    setSearchRaw(value ?? '');
  };

  const handleCompanyChange = (options = []) => {
    setSelectedCompanies(options.map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) })));
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const columns = [
    {
      field: 'license_plate',
      headerName: t('license.plateNumber', 'Plate Number'),
      flex: 1,
      valueGetter: (value, row) => row.join?.vehicle_license_plate || row.license_plate || '-'
    },
    {
      field: 'license_name',
      headerName: t('license.documentName', 'Document Name'),
      flex: 1,
      valueGetter: (value, row) => row.join?.vehicle_license_license_name || row.license_name || '-'
    },
    { field: 'license_no', headerName: t('license.documentNumber', 'Document Number'), flex: 1 },
    {
      field: 'license_exp_date',
      headerName: t('license.expiredDate', 'Expired Date'),
      flex: 1,
      valueGetter: (value, params) => {
        const expDate = params.license_exp_date ? new Date(params.license_exp_date).toLocaleDateString('id-ID') : '-';
        const isLifetime = params.join?.vehicle_license_is_lifetime === 'true' || params.join?.vehicle_license_is_lifetime === true;
        return isLifetime ? 'Lifetime' : expDate;
      }
    },
    {
      field: 'day_left',
      headerName: t('license.daysLeft', 'Days Left'),
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => {
        const dayLeft = row.day_left ?? 0;
        if (row.join?.vehicle_license_is_lifetime === 'true' || row.join?.vehicle_license_is_lifetime === true) {
          return 'Lifetime';
        }
        return dayLeft;
      }
    },
    {
      field: 'status_notif',
      headerName: t('license.status', 'Status'),
      flex: 1,
      valueGetter: (value, row) => {
        const st = row?.status_notif ?? null;
        if (st === null || st === undefined) return '-';
        if (st === 'Danger') return t('license.expired', 'Expired');
        if (st === 'Warning') return t('license.warning', 'Warning');
        if (st === 'Not-notif') return t('license.active', 'Active');
      },
      renderCell: (params) => {
        const st = String(params.row?.status_notif ?? '');
        if (st === 'Danger') {
          return <Chip label={t('license.expired', 'Expired')} color="error" variant="outlined" size="small" />;
        }
        if (st === 'Warning') {
          return <Chip label={t('license.warning', 'Warning')} color="warning" variant="outlined" size="small" />;
        }
        if (st === 'Not-notif') {
          return <Chip label={t('license.active', 'Active')} color="success" variant="outlined" size="small" />;
        }
        return '-';
      }
    },

    {
      field: 'action',
      headerName: t('license.action', 'Action'),
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu
          row={params.row}
          onEdit={(id) => {
            setEditingId(id);
            setEditingRow(params.row);
            setEditingType('vehicle-license');
            setOpenDialog(true);
          }}
          mode="inline"
          hideDelete
        />
      )
    }
  ];

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const statusOptions = [
    { value: '', label: t('license.all', 'All') },
    { value: '2', label: t('license.active', 'Active') },
    { value: '1', label: t('license.warning', 'Warning') },
    { value: '0', label: t('license.expired', 'Expired') }
  ];
  return (
    <>
      <DataTable
        rows={visibleRows}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        hideAddButton={true}
        paginationMode="server"
        showStatusFilter={true}
        initialStatus={statusFilter}
        statusOptions={statusOptions}
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />
      <LicenseDetailEditDialog
        open={openDialog}
        id={editingId}
        type={editingType}
        licenseName={editingRow?.join?.vehicle_license_license_name || editingRow?.license_name || ''}
        targetName={editingRow?.join?.vehicle_license_plate || editingRow?.license_plate || ''}
        isLifetime={
          editingRow?.join?.vehicle_license_is_lifetime === 'true' ||
          editingRow?.join?.vehicle_license_is_lifetime === true ||
          editingRow?.is_lifetime === true ||
          editingRow?.is_lifetime === 'true'
        }
        onClose={() => setOpenDialog(false)}
        onSuccess={() => {
          setOpenDialog(false);
          fetchData();
        }}
      />
    </>
  );
}

export default function VehicleLicensePage() {
  return (
    <>
      <VehicleLicensePageContent />
    </>
  );
}
